import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Grid,
    FormHelperText,
    FormControl,
    Typography,
    Paper,
    Box,
    InputAdornment,
    Divider,
    Checkbox,
    FormControlLabel,
    MenuItem,
    InputLabel,
    Select, Tooltip
} from "@mui/material";
import {
    Business as BusinessIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    LocalPrintshop as LocalPrintshopIcon,
    ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import {
    SaveQuote,
    UpdateQuote,
    GetQuoteById,
    DuplicateQuoteById,
    PrintQuote,
} from "../../../vlp/services/QuotesService";
import CommonLoader from "../common/CommonLoader";
import ClientSelectorDialog from "../common/ClientSelectorDialog";
import MultiEmailInput from '../common/MultiEmailInput';
import UserSelectorDialog from "../common/UserSelectorDialog";
import QuotransTable from "./QuotransTable"
import QuoteCommentSelectorDialog from "./QuoteCommentSelectorDialog"
import DuplicateQuoteDialog from "./DuplicateQuoteDialog"
import { useNavigate, useParams } from "react-router-dom";
import { quoteStatusList } from '../common/statusLists';
import { ArrowBack, ArrowForward } from "@mui/icons-material";

const AddQuote = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    // State variables matching QuotesDto
    const [formData, setFormData] = useState({
        quoteNumber: "",
        accountNumber: "",
        clientCode: "",
        poNumber: "",
        projectType: "",
        salesPerson: "",
        clientJobNumber: "",
        globalJobNumber: "",
        billingName: "",
        billingContact: "",
        billingAddress: "",
        billingAddress2: "",
        billingCity: "",
        billingState: "",
        billingZip: "",
        billingPhone: "",
        billingFax: "",
        billingEmail: "",
        dateCreated: new Date().toISOString().split("T")[0],
        expirationDate: new Date().toISOString().split("T")[0],
        startDate: new Date().toISOString().split("T")[0],
        createdBy: "",
        quotedAmount: 0,
        listPrice: 0,
        adjustableTotal: 0,
        nonadjustableTotal: 0,
        discountPercent: 0,
        surchargePercent: 0,
        salesTaxPercent: 0,
        discountAmount: 0,
        surchargeAmount: 0,
        miscellaneousCharges: 0,
        applyLineItemDiscount: false,
        applyDiscToMisc: false,
        quoteByJob: false,
        projectManager: "",
        template: "",
        description: "",
        internalComments: "",
        comments: "",
        status: "",
        recordid: 0,
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [quoteTrans, setQuoteTrans] = useState([]);
    const [openCommentDialog, setOpenCommentDialog] = useState(false);
    const [quoteComment, setQuoteComment] = useState("");

    // States for ClientSelectorDialog
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // States for UserSelectorDialog - Project Manager
    const [managerSelectorOpen, setManagerSelectorOpen] = useState(false);
    const [selectedManager, setSelectedManager] = useState(null);

    // States for UserSelectorDialog - Sales Person
    const [salesPersonSelectorOpen, setSalesPersonSelectorOpen] = useState(false);
    const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);

    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

    useEffect(() => {
        if (id) {
            fetchQuoteData(id);
        }
    }, [id]);

    const fetchQuoteData = async (quoteId) => {
        setLoading(true);
        try {
            const response = await GetQuoteById(quoteId);
            if (response && response.results) {
                const data = response.results;
                setFormData({
                    quoteNumber: data.quoteNumber || "",
                    accountNumber: data.accountNumber || "",
                    clientCode: data.clientCode || "",
                    poNumber: data.poNumber || "",
                    projectType: data.projectType || "",
                    salesPerson: data.salesPerson || "",
                    clientJobNumber: data.clientJobNumber || "",
                    globalJobNumber: data.globalJobNumber || "",
                    billingName: data.billingName || "",
                    billingContact: data.billingContact || "",
                    billingAddress: data.billingAddress || "",
                    billingAddress2: data.billingAddress2 || "",
                    billingCity: data.billingCity || "",
                    billingState: data.billingState || "",
                    billingZip: data.billingZip || "",
                    billingPhone: data.billingPhone || "",
                    billingFax: data.billingFax || "",
                    billingEmail: data.billingEmail || "",
                    dateCreated: data.dateCreated
                        ? data.dateCreated.split("T")[0]
                        : new Date().toISOString().split("T")[0],
                    expirationDate: data.expirationDate
                        ? data.expirationDate.split("T")[0]
                        : "",
                    startDate: data.startDate
                        ? data.startDate.split("T")[0]
                        : "",
                    createdBy: data.createdBy || "",
                    quotedAmount: data.quotedAmount || 0,
                    listPrice: data.listPrice || 0,
                    adjustableTotal: data.adjustableTotal || 0,
                    nonadjustableTotal: data.nonadjustableTotal || 0,
                    discountPercent: data.discountPercent || 0,
                    surchargePercent: data.surchargePercent || 0,
                    salesTaxPercent: data.salesTaxPercent || 0,
                    discountAmount: data.discountAmount || 0,
                    surchargeAmount: data.surchargeAmount || 0,
                    miscellaneousCharges: data.miscellaneousCharges || 0,
                    applyLineItemDiscount: data.applyLineItemDiscount || false,
                    applyDiscToMisc: data.applyDiscToMisc || false,
                    quoteByJob: data.quoteByJob || false,
                    projectManager: data.projectManager || "",
                    template: data.template || "",
                    description: data.description || "",
                    internalComments: data.internalComments || "",
                    comments: data.comments || "",
                    status: data.status || "",
                    recordid: data.recordid || 0,
                });
                setQuoteTrans(data.quotrans)
                // Set selected client if accountNumber exists
                if (data.accountNumber) {
                    setSelectedClient({
                        accountNumber: data.accountNumber,
                        clientName: data.clientCode || ""
                    });
                }

                // Set selected manager if projectManager exists
                if (data.projectManager) {
                    setSelectedManager({
                        operator: data.projectManager,
                        aliasName: ""
                    });
                }

                // Set selected sales person if salesPerson exists
                if (data.salesPerson) {
                    setSelectedSalesPerson({
                        operator: data.salesPerson,
                        aliasName: ""
                    });
                }
            } else {
                NotificationManager.error("Failed to fetch quote data");
            }
        } catch (error) {
            NotificationManager.error("Error fetching quote data");
        } finally {
            setLoading(false);
        }
    };

    const goToQuote = (newNumber) => {
        navigate(`/vlp/quotes/2025-${newNumber}`);
    };

    const handleForward = () => {
        const parts = id.split("-");
        const number = parseInt(parts[1], 10);
        goToQuote(number + 1);
    };

    const handleBackward = () => {
        const parts = id.split("-");
        const number = parseInt(parts[1], 10);

        if (number > 1) {
            goToQuote(number - 1);
        }
    };


    // Handle client selection from dialog
    const handleClientSelect = (client) => {
        if (client) {
            setSelectedClient(client);
            setFormData((prev) => ({
                ...prev,
                accountNumber: client.accountNumber,
                clientCode: client.clientName || client.clientCode || ""
            }));
            setErrors((prev) => ({
                ...prev,
                accountNumber: "",
            }));
        }
    };

    // Handle project manager selection from dialog
    const handleManagerSelect = (user) => {
        if (user) {
            setSelectedManager(user);
            setFormData((prev) => ({
                ...prev,
                projectManager: user.operator
            }));
        }
    };

    const handleCommentSelect = (comment) => {
        setFormData(prev => ({
            ...prev,
            comments: comment
        }));
        setOpenCommentDialog(false);
    };



    // Handle sales person selection from dialog
    const handleSalesPersonSelect = (user) => {
        if (user) {
            setSelectedSalesPerson(user);
            setFormData((prev) => ({
                ...prev,
                salesPerson: user.operator
            }));
        }
    };

    const numericFields = [
        "adjustableTotal",
        "nonadjustableTotal",
        "quotedAmount",
        "listPrice",
        "discountPercent",
        "discountAmount",
        "surchargePercent",
        "surchargeAmount",
        "salesTaxPercent",
        "miscellaneousCharges",
    ];

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (numericFields.includes(field)) {
            value = value === "" ? null : parseFloat(value);
        }

        if (e.target.type === "checkbox") {
            value = e.target.checked;
        }

        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.accountNumber)
            newErrors.accountNumber = "Account Number is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            var newId = "";
            if (id) {
                const response = await UpdateQuote(formData);
                if (response.status === 200) {
                    NotificationManager.success("Quote updated successfully");
                    window.location.reload();
                } else {
                    throw new Error("Failed to update quote");
                }
            } else {
                const response = await SaveQuote(formData);

                if (response.statusCode === 200) {
                    NotificationManager.success("Quote added successfully");
                    navigate("/vlp/quotes/" + response.results);
                } else {
                    throw new Error("Failed to add quote");
                }
            }

        } catch (error) {
            NotificationManager.error(error.message || "Error saving quote");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/vlp/quotes");
        window.close();
    };

    const handleDuplicateClick = () => {
        if (!id) return;
        setIsDuplicateDialogOpen(true);
    };

    const handleDuplicateConfirm = async (copies) => {
        if (!id) return;

        setLoading(true);

        try {
            const response = await DuplicateQuoteById(id, copies);
            const newQuoteNumbers = response?.results || [];

            if (newQuoteNumbers.length === 0) {
                NotificationManager.error("Failed to duplicate quote");
                return;
            }

            setIsDuplicateDialogOpen(false);

            NotificationManager.success(
                `${newQuoteNumbers.length} quote(s) created: ${newQuoteNumbers.join(", ")}`
            );

            // Open the first copy so the user lands on something; the rest are
            // reachable from the quotes search screen.
            navigate(`/vlp/quotes/${newQuoteNumbers[0]}`);

        } catch (err) {
            NotificationManager.error("Error duplicating quote");
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        if (!id) return;

        setLoading(true);

        try {
            await PrintQuote(id);
            NotificationManager.success("Quote downloaded successfully");
        } catch (err) {
            NotificationManager.error("Error generating quote document");
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <Paper elevation={3} sx={{ padding: 3 }}>
                {id && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            gap: 2,
                            mb: 1
                        }}
                    >
                        <Tooltip title="Go to previous quote">
                            <ArrowBack
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={handleBackward}
                            />
                        </Tooltip>

                        <Tooltip title="Go to next quote">
                            <ArrowForward
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={handleForward}
                            />
                        </Tooltip>

                        {/* Duplicate button stays LEFT with nav buttons */}
                        <Tooltip title="Duplicate this quote">
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleDuplicateClick}
                            >
                                Duplicate
                            </Button>
                        </Tooltip>

                        <Tooltip title="Print this quote">
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<LocalPrintshopIcon />}
                                onClick={handlePrint}
                            >
                                Print
                            </Button>
                        </Tooltip>
                    </Box>
                )}



                <Typography variant="h5" gutterBottom>
                    {id ? "Edit Quote" : "Add New Quote"}
                </Typography>

                {/* Basic Information */}
                <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Basic Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>

                <Grid container spacing={2}>

                    {id && <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Quote Number"
                            value={formData.quoteNumber}
                            onChange={handleChange("quoteNumber")}
                            fullWidth
                            required
                            size="small"
                            disabled

                        />
                    </Grid>
                    }

                    {/* Account Number - Updated with ClientSelectorDialog */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Account Number"
                            value={
                                selectedClient
                                    ? `${formData.accountNumber.trim()} - ${selectedClient.clientName.trim()}`
                                    : "Click to select client"
                            }
                            size="small"
                            fullWidth
                            required
                            onClick={() => setClientDialogOpen(true)}
                            InputProps={{
                                readOnly: true,
                                style: { cursor: "pointer" },
                                startAdornment: selectedClient ? null : (
                                    <InputAdornment position="start">
                                        <BusinessIcon color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: selectedClient ? (
                                    <InputAdornment position="end">
                                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                ) : null,
                            }}
                            helperText={
                                errors.accountNumber ||
                                (selectedClient
                                    ? "Click to change client"
                                    : "Click to select a client")
                            }
                            error={!!errors.accountNumber}
                        />
                    </Grid>

                    {/* Sales Person - Updated with UserSelectorDialog */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Sales Person"
                            value={
                                selectedSalesPerson
                                    ? `${selectedSalesPerson.operator.trim()}${selectedSalesPerson.aliasName ? ` - ${selectedSalesPerson.aliasName.trim()}` : ""}`
                                    : "Click to select sales person"
                            }
                            size="small"
                            fullWidth
                            onClick={() => setSalesPersonSelectorOpen(true)}
                            InputProps={{
                                readOnly: true,
                                style: { cursor: "pointer" },
                                startAdornment: selectedSalesPerson ? null : (
                                    <InputAdornment position="start">
                                        <PersonIcon color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: selectedSalesPerson ? (
                                    <InputAdornment position="end">
                                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                ) : null,
                            }}
                            helperText={
                                selectedSalesPerson
                                    ? "Click to change sales person"
                                    : "Click to select sales person"
                            }
                        />
                    </Grid>

                    {/* Project Manager - Updated with UserSelectorDialog */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Project Manager"
                            value={
                                selectedManager
                                    ? `${selectedManager.operator.trim()}${selectedManager.aliasName ? ` - ${selectedManager.aliasName.trim()}` : ""}`
                                    : "Click to select manager"
                            }
                            size="small"
                            fullWidth
                            onClick={() => setManagerSelectorOpen(true)}
                            InputProps={{
                                readOnly: true,
                                style: { cursor: "pointer" },
                                startAdornment: selectedManager ? null : (
                                    <InputAdornment position="start">
                                        <PersonIcon color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: selectedManager ? (
                                    <InputAdornment position="end">
                                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                ) : null,
                            }}
                            helperText={
                                selectedManager
                                    ? "Click to change manager"
                                    : "Click to select manager"
                            }
                        />
                    </Grid>



                    {id && <>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="PO Number"
                                value={formData.poNumber}
                                onChange={handleChange("poNumber")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Project Type"
                                value={formData.projectType}
                                onChange={handleChange("projectType")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Date Created"
                                type="date"
                                value={formData.dateCreated}
                                onChange={handleChange("dateCreated")}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                required
                                size="small"
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Start Date"
                                type="date"
                                value={formData.startDate}
                                onChange={handleChange("startDate")}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Expiration Date"
                                type="date"
                                value={formData.expirationDate}
                                onChange={handleChange("expirationDate")}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>

                        {/* Created By - Disabled */}
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Created By"
                                value={formData.createdBy}
                                onChange={handleChange("createdBy")}
                                fullWidth
                                size="small"
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Template"
                                value={formData.template}
                                onChange={handleChange("template")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status}
                                    onChange={handleChange("status")}
                                    label="Status"
                                >
                                    <MenuItem value="">
                                        <em>Select Status</em>
                                    </MenuItem>
                                    {quoteStatusList.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                    }

                </Grid>

                {id && <>
                    {/* Billing Information */}
                    <Box sx={{ mt: 4, mb: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Billing Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Name"
                                value={formData.billingName}
                                onChange={handleChange("billingName")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Contact"
                                value={formData.billingContact}
                                onChange={handleChange("billingContact")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Phone"
                                value={formData.billingPhone}
                                onChange={handleChange("billingPhone")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Fax"
                                value={formData.billingFax}
                                onChange={handleChange("billingFax")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Address"
                                value={formData.billingAddress}
                                onChange={handleChange("billingAddress")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Address 2"
                                value={formData.billingAddress2}
                                onChange={handleChange("billingAddress2")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing City"
                                value={formData.billingCity}
                                onChange={handleChange("billingCity")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing State"
                                value={formData.billingState}
                                onChange={handleChange("billingState")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Billing Zip"
                                value={formData.billingZip}
                                onChange={handleChange("billingZip")}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <MultiEmailInput
                                value={formData.billingEmail}
                                onChange={handleChange("billingEmail")}
                                label="Billing Emails"
                                required
                            />
                        </Grid>

                    </Grid>

                    {/* Financial Information */}
                    <Box sx={{ mt: 4, mb: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Financial Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Quoted Amount"
                                value={formData.quotedAmount}
                                onChange={handleChange("quotedAmount")}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="List Price"
                                value={formData.listPrice}
                                onChange={handleChange("listPrice")}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Discount Percent"
                                value={formData.discountPercent}
                                onChange={handleChange("discountPercent")}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Discount Amount"
                                value={formData.discountAmount}
                                onChange={handleChange("discountAmount")}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Miscellaneous Charges"
                                value={formData.miscellaneousCharges}
                                onChange={handleChange("miscellaneousCharges")}
                                fullWidth
                                size="small"
                                type="number"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </>}



                {/* Options */}
                <Box sx={{ mt: 4, mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Options
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.applyLineItemDiscount}
                                    onChange={handleChange("applyLineItemDiscount")}
                                />
                            }
                            label="Apply Line Item Discount"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.applyDiscToMisc}
                                    onChange={handleChange("applyDiscToMisc")}
                                />
                            }
                            label="Apply Discount to Misc"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.quoteByJob}
                                    onChange={handleChange("quoteByJob")}
                                />
                            }
                            label="Quote By Job"
                        />
                    </Grid>
                </Grid>

                {/* Description and Comments */}
                <Box sx={{ mt: 4, mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Additional Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Internal Comments"
                            value={formData.internalComments}
                            onChange={handleChange("internalComments")}
                            fullWidth
                            multiline
                            rows={6}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={handleChange("description")}
                            fullWidth
                            multiline
                            rows={6}
                            size="small"
                        />
                    </Grid>


                    {id && <Grid item xs={12} md={6}>
                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label="Comments"
                                value={formData.comments}
                                onChange={handleChange("comments")}
                                fullWidth
                                multiline
                                rows={6}
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setOpenCommentDialog(true)}
                                                sx={{ minWidth: 'auto', px: 1 }}
                                            >
                                                Select
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <QuoteCommentSelectorDialog
                            open={openCommentDialog}
                            onClose={() => setOpenCommentDialog(false)}
                            onSelectComment={handleCommentSelect}
                        />
                    </Grid>}




                </Grid>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={handleCancel} color="secondary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained">
                        {id ? "Update" : "Save"}
                    </Button>
                </Box>

                {id && (
                    <Box sx={{ mt: 3 }}>
                        <QuotransTable quotransData={quoteTrans} quoteNumber={formData.quoteNumber}
                            accountNumber={formData.accountNumber} />
                    </Box>
                )}

            </Paper>

            <CommonLoader loading={loading} />

            {/* Client Selector Dialog */}
            <ClientSelectorDialog
                open={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                onSelect={handleClientSelect}
                multiSelect={false}
            />

            {/* User Selector Dialog - Project Manager */}
            <UserSelectorDialog
                open={managerSelectorOpen}
                onClose={() => setManagerSelectorOpen(false)}
                onSelect={handleManagerSelect}
                multiSelect={false}
            />

            {/* User Selector Dialog - Sales Person */}
            <UserSelectorDialog
                open={salesPersonSelectorOpen}
                onClose={() => setSalesPersonSelectorOpen(false)}
                onSelect={handleSalesPersonSelect}
                multiSelect={false}
            />

            {/* Duplicate Quote Dialog - asks how many copies to create */}
            <DuplicateQuoteDialog
                open={isDuplicateDialogOpen}
                quoteNumber={id}
                loading={loading}
                onClose={() => setIsDuplicateDialogOpen(false)}
                onConfirm={handleDuplicateConfirm}
            />
        </>
    );
};

export default AddQuote;